import React, { useMemo, useRef, useContext, useEffect } from 'react';
import Editor, { BeforeMount, OnMount } from '@monaco-editor/react';
import { WorkspaceContext } from '../WorkspaceContext';

function defineFlashroomThemes(m: any) {
  // define once per instance
  if ((m as any).__flashroomThemesDefined__) return;
  m.editor.defineTheme('flashroom-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: [],
    colors: {
      'editor.background': '#0d1422',
      'editor.foreground': '#ffffff',
      'editorCursor.foreground': '#ffffff',
      'editorLineNumber.foreground': '#999999',
      'editorLineHighlightBackground': '#1a2332',
    },
  });
  m.editor.defineTheme('flashroom-light', {
    base: 'vs', // light base
    inherit: true,
    rules: [],
    colors: {
      'editor.background': '#ffffff',  // your subtle light grey
      'editor.foreground': '#0d1422',
      'editorCursor.foreground': '#ef233c',
      'editorLineNumber.foreground': '#8992a3',
      'editorLineHighlightBackground': '#eef1f4',
      'editor.selectionBackground': '#dbe0e6aa',
      'editor.inactiveSelectionBackground': '#e6e9eeaa',
    },
  });
  (m as any).__flashroomThemesDefined__ = true;
}

export default function CodeEditor({
  onUpdate,
  fileLanguage,
  isActive,
  initialText,
  modelPath,
  readOnly,
}: {
  onUpdate?: (p: { editorRef: any }) => void;
  fileLanguage: string;
  isActive?: boolean;
  initialText?: string;
  modelPath: string;
  readOnly: boolean;
}) {
  const { ctheme } = useContext(WorkspaceContext)!;
  const editorRef = useRef<any>(null);

  const bootText = useMemo(() => {
    if (typeof initialText === 'string') return initialText;
    const map: Record<string, string> = {
      javascript: '// type your code here',
      typescript: '// type your code here',
      python: '# type your code here',
      html: '<!-- type your code here -->',
      css: '/* type your code here */',
      json: '// type your code here',
      sql: '-- type your code here',
      java: '// type your code here',
      c: '/* type your code here */',
      cpp: '/* type your code here */',
      php: '// type your code here',
      shell: '# type your code here',
    };
    return map[fileLanguage?.toLowerCase()] ?? '// type your code here';
  }, [fileLanguage, initialText]);

  const beforeMount: BeforeMount = (m) => {
    // define themes before the editor instance is created
    defineFlashroomThemes(m);
  };

  const onMount: OnMount = (editor, m) => {
    editorRef.current = editor;
    editor.updateOptions({
      automaticLayout: true,
      wordWrap: "on",
      padding: {
        top: 12,
        bottom: 12
      },
    });

    // bind a unique model per path
    const uri = m.Uri.parse(modelPath);
    let model = m.editor.getModel(uri);
    if (!model) model = m.editor.createModel(bootText, fileLanguage, uri);
    editor.setModel(model!);

    onUpdate?.({ editorRef: editor });
  };

  // optional: relayout when becoming active — swallow errors from calling
  // .layout() on an instance mid-disposal (a known monaco-editor/React 19
  // StrictMode race when many editors are mounted at once, e.g. reordering
  // open tabs re-renders every open editor's isActive prop together); the
  // editor recovers fine on its own, this only stops that race from
  // surfacing as an uncaught error that takes down the whole page.
  useEffect(() => {
    if (!isActive || !editorRef.current) return;
    try {
      editorRef.current.layout();
    } catch {
      // ignore — see comment above
    }
  }, [isActive]);

  const themeName = ctheme === 'dark' ? 'flashroom-dark' : 'flashroom-light';

  return (
    <Editor
      key={themeName}                // <— forces a clean remount on theme change
      height="100%"
      defaultLanguage={fileLanguage}
      defaultValue={bootText}
      path={modelPath}               // <— ensure UNIQUE per snippet/editor
      beforeMount={beforeMount}
      onMount={onMount}
      theme={themeName}

      // <— applied after beforeMount defines it
      options={{
        automaticLayout: true,
        readOnly: readOnly,
        fontSize: 14,
        fontFamily: 'Fira Code, monospace',
        minimap: { enabled: false },
        lineNumbers: 'on',
        scrollBeyondLastLine: false,
      }}
    />
  );
}



